import { useTimeZone } from '@/auth/useTimeZone';
import { SessionFeedbackForm } from '@/components/feedbacks/session-feedback-form';
import {
  AssessmentResultForm,
  buildUpdateAssessmentResultsPayload,
  getAssessmentFormatValidationSchema,
  getAssessmentUISection,
  SessionFeedbackFormSchema,
  sessionFeedbackFormSchema,
  useSessionFeedbackCommonData,
} from '@/components/feedbacks/session-feedback-utils';
import { InfoPage } from '@/components/info-page';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  getCoachViewableSessionFeedback,
  getCoachViewableSessionFeedbacks,
  getSessionFeedbackTimeTracking,
  recordSessionFeedbackTimeTracking,
  updateSessionFeedback,
} from '@/lib/api-calls';
import { scrollToTopOfScrollableContainer } from '@/lib/device-utils';
import { shortTermStorage } from '@/lib/short-term-storage';
import {
  booleanFromString,
  booleanToString,
  formatInstant,
  InstantCompare,
  isTrialLessonName,
  nullsToUndefined,
} from '@airreading/common';
import { OrgFeedbackType, UpdateSessionFeedbackOptions } from '@airreading/common/data';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ClockArrowUp, Minimize2, SquareCheck, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultValues, FieldErrors, useForm, UseFormReturn, useFormState } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { AirForm } from '../forms/auto-save-form';
import { resolveIssuesForSave } from './feedback-issues';

/**
 * Handle exposed to parent component to save and close the feedback form.
 * Used when clicking outside the drawer to automatically save as draft before closing.
 */
export interface SaveAndCloseHandle {
  /**
   * Automatically saves the current form state as draft and returns whether the drawer can be closed.
   * If validation fails, shows confirmation dialog instead.
   * @returns Promise resolving to { canClose: boolean, showConfirmation: boolean }
   */
  saveAndClose: () => Promise<{ canClose: boolean; showConfirmation: boolean }>;
  /**
   * Checks if there are unsaved changes without attempting to save.
   * @returns boolean indicating if there are unsaved changes
   */
  hasUnsavedChanges: () => boolean;
  /**
   * Cleans up localStorage when closing is confirmed.
   */
  cleanupOnClose: () => void;
}

interface CoachViewableSessionFeedbackPageProps {
  feedbackCompoundId: {
    coachId: string;
    sessionId: string;
    studentId: string;
  };
  onClose?: (bypassConfirmation?: boolean) => void;
  setShouldConfirmOnClose?: (value: boolean) => void;
  onFullPageChange?: (isFullPage: boolean) => void;
  isFullPage?: boolean;
  /**
   * Ref to expose saveAndClose function to parent component.
   * Used to save form as draft when clicking outside the drawer.
   */
  saveAndCloseRef?: React.MutableRefObject<SaveAndCloseHandle | null>;
  /**
   * Function to show the confirmation dialog immediately.
   * Used to show "Unsaved changes" dialog when validation fails.
   */
  showConfirmation?: () => void;
  /**
   * When true, skips the large in-flow student name + needs badges row (e.g. drawer title already shows them).
   */
  suppressStudentTitleRow?: boolean;
}

export function CoachViewableSessionFeedbackPage({
  feedbackCompoundId,
  onClose: onCloseCallback,
  setShouldConfirmOnClose,
  onFullPageChange,
  isFullPage: externalIsFullPage,
  saveAndCloseRef,
  showConfirmation,
  suppressStudentTitleRow = false,
}: CoachViewableSessionFeedbackPageProps) {
  const abort = useRef<AbortController | null>(null);
  const syncExecutedRef = useRef(false);
  const sessionFeedbackTimeTrackingGateRef = useRef(false);
  const [internalIsFullPage, setInternalIsFullPage] = useState(false);
  const [localStorageSavedAt, setLocalStorageSavedAt] = useState<Temporal.Instant | null>(null);
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const isFullPage = externalIsFullPage ?? internalIsFullPage;
  const setIsFullPage = (value: boolean) => {
    if (externalIsFullPage === undefined) {
      setInternalIsFullPage(value);
    }
    onFullPageChange?.(value);
  };
  const formContainerRef = useRef<HTMLDivElement>(null);

  const { data: sessionFeedback, error } = useSuspenseQuery(getCoachViewableSessionFeedback(feedbackCompoundId));
  const { data: sessionFeedbackTimeTrackingExists } = useQuery(getSessionFeedbackTimeTracking(feedbackCompoundId));
  const { mutateAsync, isPending: isUpdateSessionFeedbackPending } = useMutation({
    mutationFn: async (data: UpdateSessionFeedbackOptions) => {
      if (abort.current) {
        abort.current.abort();
      }

      abort.current = new AbortController();
      const signal = abort.current?.signal;
      return updateSessionFeedback(data, signal);
    },
  });

  const { timeZone } = useTimeZone();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    student,
    studentAttended,
    feedbackContent,
    majorConcerns,
    otherTechIssues,
    issues,
    feedbackSubmitted,
    submittedAtInstant,
    draftSavedAtInstant,
  } = sessionFeedback;

  const {
    assessmentResultsGrouped,
    assessmentContentsById,
    isDiagnosticResultAllEmpty,
    unFinishedAssessmentSections,
    assessmentResultsDefaultValues,
    hasIncompleteDiagnosticLists,
  } = useSessionFeedbackCommonData(sessionFeedback);

  // if a student already has a level placement (learning path and starting lesson),
  // then we no longer need diagnostic feedback from this session
  const studentHasBeenLeveled = !!student.learningPathId && !!student.startingLessonId;

  const diagnosticCompletedFromOtherSession = studentHasBeenLeveled && isDiagnosticResultAllEmpty;

  const isFamilyClass = student.orgFeedbackType === OrgFeedbackType.parentFeedback;
  const isFamilyTrialClass = isFamilyClass && isTrialLessonName(sessionFeedback.session.lessonName);

  const defaultValues: DefaultValues<SessionFeedbackFormSchema> = nullsToUndefined({
    studentAttended: booleanToString(studentAttended),
    assessmentNotFinishedUiSections: [...unFinishedAssessmentSections],
    diagnosticIncomplete: hasIncompleteDiagnosticLists,
    issues: issues ?? [],
    assessmentResults: assessmentResultsDefaultValues,
    lateFeedbackDismissed: sessionFeedback.lateFeedbackDismissed,
    majorConcerns: sessionFeedback.majorConcerns,
    otherTechIssues: sessionFeedback.otherTechIssues,
    feedbackContent: feedbackContent,
  });

  const form: UseFormReturn<SessionFeedbackFormSchema> = useForm<SessionFeedbackFormSchema>({
    resolver: zodResolver(sessionFeedbackFormSchema),
    defaultValues,
  });

  const { isDirty } = useFormState(form);

  const handleFormError = useCallback(
    (errors: FieldErrors<SessionFeedbackFormSchema>) => {
      if (errors.studentAttended) {
        scrollToTopOfScrollableContainer(formContainerRef.current);

        toast({
          title: 'Student attendance field is required',
          variant: 'destructive',
        });
        return;
      }
    },
    [toast],
  );

  /**
   * Helper function to get the form key for this feedback form.
   * Encapsulates the form key generation logic to avoid repetition.
   */
  const getFeedbackFormKey = useCallback(() => {
    return `session-feedback-${feedbackCompoundId.sessionId}-${sessionFeedback.coachId}`;
  }, [feedbackCompoundId.sessionId, sessionFeedback.coachId]);

  /**
   * Helper function to get the short-term storage instance for this feedback form.
   * Encapsulates the form key generation logic to avoid repetition.
   */
  const getFeedbackStorage = useCallback(() => {
    return shortTermStorage<SessionFeedbackFormSchema>(getFeedbackFormKey(), sessionFeedbackFormSchema);
  }, [getFeedbackFormKey]);

  useEffect(() => {
    // If confirmation is already showing, keep it enabled
    if (isShowingConfirmation) {
      setShouldConfirmOnClose?.(true);
      return;
    }

    // Check if there are unsaved changes (form is dirty or localStorage has data)
    const hasUnsavedChanges = isDirty || !!localStorageSavedAt;

    if (!hasUnsavedChanges) {
      setShouldConfirmOnClose?.(false);
      return;
    }

    // If there are unsaved changes, enable confirmation dialog
    setShouldConfirmOnClose?.(true);
  }, [isDirty, localStorageSavedAt, isShowingConfirmation, setShouldConfirmOnClose]);

  const handleSubmit = useCallback(
    async (formData: z.infer<typeof sessionFeedbackFormSchema>, action: 'submit' | 'saveAsDraft'): Promise<boolean> => {
      const {
        studentAttended,
        issues: _issues,
        lateFeedbackDismissed,
        feedbackContent,
        majorConcerns: formMajorConcerns,
        otherTechIssues: formOtherTechIssues,
      } = formData;
      const { issues, majorConcerns, otherTechIssues } = resolveIssuesForSave({
        studentAttended: booleanFromString(studentAttended),
        feedbackIssues: _issues,
        majorConcerns: formMajorConcerns,
        otherTechIssues: formOtherTechIssues,
      });

      const assessmentIdsNotFinished = assessmentResultsGrouped
        .filter(({ assessment }) =>
          formData.assessmentNotFinishedUiSections.includes(getAssessmentUISection(assessment)),
        )
        .map(({ assessment }) => assessment.id);

      const studentGrade = student.grade;

      const { assessmentResultsPayload, skippedDiagnosticResultIds, hasDiagnosticAssessments } =
        buildUpdateAssessmentResultsPayload({
          formData,
          assessmentContentsById,
          assessmentIdsNotFinished,
          isSavingAsDraft: action === 'saveAsDraft',
          assessmentResultsGrouped,
          form,
          diagnosticCompletedFromOtherSession,
          studentGrade,
        });

      let manualValidationPassed = true;
      form.clearErrors();

      // Only validate if submitting (not saving as draft) and student attended
      if (action === 'submit' && studentAttended === 'true') {
        // Validate assessment results
        for (const { assessmentIndex, results, assessment } of assessmentResultsGrouped) {
          if (assessmentIdsNotFinished.includes(assessment.id)) {
            continue;
          }

          results.forEach(({ assessmentContentId, id }, resultIndex) => {
            const content = assessmentContentsById.get(assessmentContentId);
            if (!content) {
              console.warn(
                `Assessment content with ID ${assessmentContentId} not found in assessment contents by ID map.`,
              );
              return;
            }
            // Skip validation in two cases:
            // 1. The assessment result was skipped due to prior errors
            // 2. The session includes a diagnostic assessment and the student has been leveled. So skipping all GLA + Diagnostic feedbacks
            if (skippedDiagnosticResultIds.includes(id) || (hasDiagnosticAssessments && studentHasBeenLeveled)) {
              return;
            }

            const currentResultData = formData.assessmentResults[assessmentIndex].results[resultIndex];

            // Skip validation for diagnostic word lists that are not filled in when diagnosticIncomplete is checked
            if (content.format === 'diagnostic_word_list' && formData.diagnosticIncomplete) {
              const isNotFilledIn =
                !currentResultData.noErrorsMadeInWordList && currentResultData.incorrectWords.length === 0;
              if (isNotFilledIn) {
                return;
              }
            }

            const validationResults = getAssessmentFormatValidationSchema(content.format, {
              studentGrade,
              contentName: content.name,
            }).safeParse(currentResultData);
            if (!validationResults.success) {
              manualValidationPassed = false;
              validationResults.error.errors.forEach(({ path, message }) => {
                const keyName = path[0] as keyof AssessmentResultForm;
                form.setError(`assessmentResults.${assessmentIndex}.results.${resultIndex}.${keyName}`, {
                  type: 'manual',
                  message,
                });
              });
            }
          });
        }

        if (!manualValidationPassed) {
          toast({
            title: 'Please fill out the required fields or mark the section as incomplete to submit your feedback.',
            variant: 'destructive',
          });
          return false;
        }
      }

      await mutateAsync({
        ...feedbackCompoundId,
        lateFeedbackDismissed,
        feedbackContent,
        majorConcerns,
        otherTechIssues,
        issues,
        studentAttended: booleanFromString(studentAttended),
        action,

        // if student didn't attend, don't update assessment results. [] will skip updating any, not erasing existing results
        // if saving as draft, update assessment results to save partial results
        assessmentResults: studentAttended === 'true' || action === 'saveAsDraft' ? assessmentResultsPayload : [],
      });

      queryClient.invalidateQueries(getCoachViewableSessionFeedback(feedbackCompoundId));
      queryClient.invalidateQueries(getCoachViewableSessionFeedbacks());

      return true;
    },
    [
      assessmentResultsGrouped,
      assessmentContentsById,
      form,
      diagnosticCompletedFromOtherSession,
      mutateAsync,
      feedbackCompoundId,
      queryClient,
      student.grade,
      studentHasBeenLeveled,
      toast,
    ],
  );

  const handleFormAction = useCallback(
    async (formData: SessionFeedbackFormSchema, action: 'submit' | 'saveAsDraft') => {
      try {
        const isSuccess = await handleSubmit(formData, action);

        if (!isSuccess) {
          return;
        }

        // Remove localStorage when successfully saved to backend
        const storage = getFeedbackStorage();
        storage.remove();
        setLocalStorageSavedAt(null);

        toast({
          title: action === 'submit' ? 'Feedback submitted successfully' : 'Feedback saved as draft',
        });
        form.reset(formData);
        setIsShowingConfirmation(false);
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        Sentry.captureException(error, {
          tags: {
            feature: 'session-feedback',
            action,
            coachId: feedbackCompoundId.coachId,
            sessionId: feedbackCompoundId.sessionId,
            studentId: feedbackCompoundId.studentId,
          },
        });
        // If submit failed, show confirmation dialog
        setShouldConfirmOnClose?.(true);
        showConfirmation?.();
        toast({
          title: `Failed to ${action === 'submit' ? 'submit' : 'save'} feedback. Please try again or contact Air Reading support`,
        });
        form.reset(formData);
      }
    },
    [handleSubmit, toast, form, setShouldConfirmOnClose, showConfirmation, feedbackCompoundId, getFeedbackStorage],
  );

  const onSubmit = useCallback(
    async (props: SessionFeedbackFormSchema) => {
      await handleFormAction(props, 'submit');
    },
    [handleFormAction],
  );

  const onSaveAsDraft = useCallback(
    async (props: SessionFeedbackFormSchema) => {
      await handleFormAction(props, 'saveAsDraft');
    },
    [handleFormAction],
  );

  // Reset sync flag when feedback changes
  useEffect(() => {
    syncExecutedRef.current = false;
    // New session/coach/student = new unique tuple for time-tracking; allow a first-started POST again for this drawer.
    sessionFeedbackTimeTrackingGateRef.current = false;
    // Reset localStorage saved timestamp when feedback changes
    setLocalStorageSavedAt(null);
  }, [feedbackCompoundId.sessionId, feedbackCompoundId.coachId, feedbackCompoundId.studentId]);

  useEffect(() => {
    if (sessionFeedbackTimeTrackingExists === true) {
      sessionFeedbackTimeTrackingGateRef.current = true;
    }
  }, [sessionFeedbackTimeTrackingExists]);

  /**
   * Record when the coach first interacts with this feedback form (any field dirty).
   * Server dedupes on (session, coach, student, actor); we avoid spamming POST after we know a row exists or we already sent one.
   * We do not invalidate the time-tracking query on success: the gate ref is enough to stop repeats, and TanStack would keep
   * showing stale `false` until refetch anyway. Gap: nothing on this page reads server timestamps—if that is needed later, refetch or merge cache.
   */
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    if (sessionFeedbackTimeTrackingGateRef.current) {
      return;
    }
    if (sessionFeedbackTimeTrackingExists === true) {
      sessionFeedbackTimeTrackingGateRef.current = true;
      return;
    }

    // Lock immediately so rapid edits do not issue multiple POSTs before the first resolves (server still dedupes if they race).
    sessionFeedbackTimeTrackingGateRef.current = true;
    void recordSessionFeedbackTimeTracking({
      sessionId: feedbackCompoundId.sessionId,
      coachId: feedbackCompoundId.coachId,
      studentId: feedbackCompoundId.studentId,
    }).catch(() => {});
  }, [isDirty, sessionFeedbackTimeTrackingExists, feedbackCompoundId]);

  // Check for localStorage data on mount and when form changes
  useEffect(() => {
    const storage = getFeedbackStorage();
    const localStorageLastUpdated = storage.getLastUpdated();

    if (localStorageLastUpdated) {
      setLocalStorageSavedAt(Temporal.Instant.from(localStorageLastUpdated));
    } else {
      setLocalStorageSavedAt(null);
    }
  }, [
    feedbackCompoundId.sessionId,
    feedbackCompoundId.coachId,
    sessionFeedback.coachId,
    form.formState.isDirty,
    getFeedbackFormKey,
    getFeedbackStorage,
  ]);

  // Sync localStorage with backend on component mount (only once per feedback)
  // NOTE: We do NOT save localStorage to backend automatically - only when user clicks "Save draft"
  // This effect only removes localStorage if backend has newer data
  useEffect(() => {
    // Only execute sync once per feedback load
    if (syncExecutedRef.current) {
      return;
    }

    const storage = getFeedbackStorage();
    const localStorageData = storage.get();
    const localStorageLastUpdated = storage.getLastUpdated();

    // Only proceed if there's data in localStorage
    if (!localStorageLastUpdated || Object.keys(localStorageData).length === 0) {
      syncExecutedRef.current = true;
      return;
    }

    // Get the most recent timestamp from backend (either draftSavedAt or submittedAt)
    const backendMostRecentInstant =
      submittedAtInstant && draftSavedAtInstant
        ? InstantCompare(submittedAtInstant).isAfterOrEqual(draftSavedAtInstant)
          ? submittedAtInstant
          : draftSavedAtInstant
        : submittedAtInstant || draftSavedAtInstant;

    // If backend has data, compare timestamps
    if (backendMostRecentInstant) {
      const localStorageInstant = Temporal.Instant.from(localStorageLastUpdated);
      const isLocalStorageNewer = InstantCompare(localStorageInstant).isAfter(backendMostRecentInstant);

      if (!isLocalStorageNewer) {
        // Backend is newer or equal, remove localStorage (don't overwrite backend with old localStorage)
        storage.remove();
        setLocalStorageSavedAt(null);
      }
      // If localStorage is newer, keep it but don't save to backend automatically
      // User must click "Save draft" to save it
    }
    // If backend has no timestamp, keep localStorage but don't save to backend automatically
    // User must click "Save draft" to save it

    syncExecutedRef.current = true;
  }, [
    feedbackCompoundId.sessionId,
    feedbackCompoundId.coachId,
    feedbackCompoundId.studentId,
    sessionFeedback.coachId,
    submittedAtInstant,
    draftSavedAtInstant,
    getFeedbackStorage,
  ]);

  const onClose = (bypassConfirmation?: boolean) => {
    // If bypassing confirmation, close immediately and clean up
    if (bypassConfirmation) {
      const storage = getFeedbackStorage();
      storage.remove();
      setLocalStorageSavedAt(null);
      setIsShowingConfirmation(false);
      onCloseCallback?.(bypassConfirmation);
      return;
    }

    // Check if there are unsaved changes
    const hasUnsavedChanges = form.formState.isDirty || !!localStorageSavedAt;

    // If there are unsaved changes, show confirmation dialog
    if (hasUnsavedChanges) {
      setShouldConfirmOnClose?.(true);
      showConfirmation?.();
      return;
    }

    // No unsaved changes, close normally and clean up
    const storage = getFeedbackStorage();
    storage.remove();
    setLocalStorageSavedAt(null);
    setIsShowingConfirmation(false);
    onCloseCallback?.(bypassConfirmation);
  };

  /**
   * Expose saveAndClose function to parent component via ref.
   * This allows the parent (RightDrawer) to automatically save as draft
   * when the user clicks outside the drawer, before closing it.
   */
  useEffect(() => {
    if (saveAndCloseRef) {
      saveAndCloseRef.current = {
        saveAndClose: async () => {
          // Check if form has unsaved changes (form is dirty or localStorage has data)
          const hasUnsavedChanges = form.formState.isDirty || !!localStorageSavedAt;

          // If no unsaved changes, can close without saving
          if (!hasUnsavedChanges) {
            return { canClose: true, showConfirmation: false };
          }

          // Get current form values
          const formValues = form.getValues();

          // Try to save as draft automatically
          try {
            const isSuccess = await handleSubmit(formValues, 'saveAsDraft');

            if (!isSuccess) {
              // If validation failed, show confirmation dialog
              setShouldConfirmOnClose?.(true);
              showConfirmation?.();
              return { canClose: false, showConfirmation: true };
            }

            // Successfully saved - clean up localStorage and update state
            const storage = getFeedbackStorage();
            storage.remove();
            setLocalStorageSavedAt(null);

            // Invalidate queries to refresh data
            queryClient.invalidateQueries(getCoachViewableSessionFeedback(feedbackCompoundId));
            queryClient.invalidateQueries(getCoachViewableSessionFeedbacks());

            return { canClose: true, showConfirmation: false };
          } catch (error) {
            // If save fails (network error, server error, etc.), show unsaved changes confirmation popup
            if (!axios.isCancel(error)) {
              Sentry.captureException(error, {
                tags: {
                  feature: 'session-feedback',
                  action: 'saveAsDraft',
                  coachId: feedbackCompoundId.coachId,
                  sessionId: feedbackCompoundId.sessionId,
                  studentId: feedbackCompoundId.studentId,
                },
              });
              console.error('Failed to save draft on outside click:', error);
              setShouldConfirmOnClose?.(true);
              showConfirmation?.();
            }
            return { canClose: false, showConfirmation: true };
          }
        },
        hasUnsavedChanges: () => {
          // Check if form has unsaved changes (form is dirty or localStorage has data)
          return form.formState.isDirty || !!localStorageSavedAt;
        },
        cleanupOnClose: () => {
          // Clean up localStorage when closing is confirmed
          const storage = getFeedbackStorage();
          storage.remove();
          setLocalStorageSavedAt(null);
        },
      };
    }
  }, [
    form,
    handleSubmit,
    setShouldConfirmOnClose,
    saveAndCloseRef,
    showConfirmation,
    localStorageSavedAt,
    queryClient,
    feedbackCompoundId,
    sessionFeedback.coachId,
    getFeedbackStorage,
  ]);

  const formatTimestamp = useCallback((instant: Temporal.Instant, timeZone: string) => {
    const formatted = formatInstant(instant, timeZone);
    return `${formatted.formattedDateWithoutYear} ${formatted.formattedTime.toLowerCase()}`;
  }, []);

  const submitedAndSavedInfo = useMemo(() => {
    // Determine which timestamps to show based on requirements
    const shouldShowSubmitted = !!submittedAtInstant;
    let shouldShowSaved = !!draftSavedAtInstant;

    if (shouldShowSubmitted && shouldShowSaved && submittedAtInstant && draftSavedAtInstant) {
      // If both timestamps exist, compare them to determine display logic
      // If submitted is on or after saved, hide the saved timestamp because it means the early 'saved' data is already submitted
      if (InstantCompare(submittedAtInstant).isAfterOrEqual(draftSavedAtInstant)) {
        shouldShowSaved = false;
      }
    }

    const submittedAtText =
      shouldShowSubmitted && submittedAtInstant ? (
        <div className="flex items-center gap-1">
          <SquareCheck className="h-3.5 w-3.5 text-green-800" />
          <span>Feedback has been submitted at {formatTimestamp(submittedAtInstant, timeZone)}</span>
        </div>
      ) : null;

    const draftedAtText =
      shouldShowSaved && draftSavedAtInstant ? (
        <div className="flex items-center gap-1 font-normal leading-4 tracking-tight text-slate-500">
          <ClockArrowUp className="h-3.5 w-3.5" />
          <span>Feedback draft has been saved at {formatTimestamp(draftSavedAtInstant, timeZone)}</span>
        </div>
      ) : null;

    // Show localStorage autosave message only if:
    // 1. There's localStorage data saved
    // 2. localStorage is newer than the most recent backend timestamp (either submittedAt or draftSavedAt)
    const mostRecentBackendTimestamp =
      submittedAtInstant && draftSavedAtInstant
        ? InstantCompare(submittedAtInstant).isAfterOrEqual(draftSavedAtInstant)
          ? submittedAtInstant
          : draftSavedAtInstant
        : submittedAtInstant || draftSavedAtInstant;

    const shouldShowLocalStorage =
      localStorageSavedAt &&
      (!mostRecentBackendTimestamp || InstantCompare(localStorageSavedAt).isAfter(mostRecentBackendTimestamp));
    const localStorageSavedText = shouldShowLocalStorage ? (
      <div className="flex items-center gap-1 font-normal leading-4 tracking-tight text-slate-400">
        <ClockArrowUp className="h-3.5 w-3.5" />
        <span>Feedback autosaved at {formatTimestamp(localStorageSavedAt, timeZone)}</span>
      </div>
    ) : null;

    return (
      <div className="text-xs flex flex-col gap-1">
        {submittedAtText}
        {draftedAtText}
        {localStorageSavedText}
      </div>
    );
  }, [submittedAtInstant, draftSavedAtInstant, localStorageSavedAt, timeZone, formatTimestamp]);

  if (error) {
    return (
      <InfoPage headerText="Feedback">
        <div>
          We are sorry, but we could not fetch your feedback. Please notify Air Reading about this issue or try again
          later.
        </div>
      </InfoPage>
    );
  }

  const studentName = student.coachFacingDisplayName || 'Student';
  const studentGrade = student.grade;

  return (
    <div
      className={
        isFullPage
          ? 'fixed top-0 right-0 bottom-0 left-[var(--sidebar-width,15rem)] z-50 bg-[#F7F7F9] overflow-auto'
          : 'relative'
      }
    >
      {isFullPage && (
        <div className="sticky top-0 z-10 bg-[#F7F7F9] border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold capitalize shrink-0">{studentName}</h2>
            <StudentNeedsIndicators
              specialEd={student.specialEd}
              dyslexic={student.dyslexic}
              ell={student.ell}
              mode="full-text"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setIsFullPage(false)}
              title="Minimize"
              className="h-8 w-8 p-0"
              data-testid="feedback-button-minimize"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onClose(false)}
              title="Close"
              className="h-8 w-8 p-0"
              data-testid="feedback-button-close-fullpage"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className={isFullPage ? 'p-4' : ''} ref={formContainerRef}>
        {!isFullPage && !suppressStudentTitleRow && (
          <div className="mb-3 flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="text-[30px] font-semibold capitalize">{studentName}</h2>
            <StudentNeedsIndicators
              specialEd={student.specialEd}
              dyslexic={student.dyslexic}
              ell={student.ell}
              mode="full-text"
            />
          </div>
        )}
        <AirForm
          form={form}
          autosaveOptions={{
            isSubmitting: isUpdateSessionFeedbackPending,
            shortTermPersistent: {
              formKey: getFeedbackFormKey(),
              onSubmit: () => {
                // Update localStorage saved timestamp when autosave occurs
                const storage = getFeedbackStorage();
                const localStorageLastUpdated = storage.getLastUpdated();
                if (localStorageLastUpdated) {
                  setLocalStorageSavedAt(Temporal.Instant.from(localStorageLastUpdated));
                }
              },
            },
            delay: Temporal.Duration.from({ seconds: 5 }),
          }}
        >
          <form onSubmit={form.handleSubmit((e) => onSubmit(e), handleFormError)}>
            <div className="flex flex-col">
              <>
                <SessionFeedbackForm
                  editing={true}
                  form={form}
                  studentAttended={studentAttended}
                  issues={issues}
                  feedbackContent={feedbackContent}
                  majorConcerns={majorConcerns}
                  otherTechIssues={otherTechIssues}
                  assessmentResults={assessmentResultsGrouped}
                  feedbackSubmitted={feedbackSubmitted}
                  assessmentContentsById={assessmentContentsById}
                  diagnosticCompletedFromOtherSession={diagnosticCompletedFromOtherSession}
                  studentName={studentName}
                  studentGrade={studentGrade}
                  isFamilyClass={isFamilyClass}
                  isFamilyTrialClass={isFamilyTrialClass}
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    loading={form.formState.isSubmitting}
                    size={'sm'}
                    variant="destructive"
                    data-testid="feedback-button-submit"
                  >
                    Submit feedback
                  </Button>
                  {!feedbackSubmitted && (
                    <Button
                      type="button"
                      onClick={() => onSaveAsDraft(form.getValues())}
                      loading={isUpdateSessionFeedbackPending}
                      size={'sm'}
                      variant="dismiss"
                      data-testid="feedback-button-save-draft"
                    >
                      Save draft
                    </Button>
                  )}
                  {!isFullPage && (
                    <Button
                      type="button"
                      size={'sm'}
                      variant="dismiss"
                      onClick={() => onClose(false)}
                      data-testid="feedback-button-close-form"
                    >
                      Close
                    </Button>
                  )}
                </div>
                <span className="mt-4">{submitedAndSavedInfo}</span>
              </>
            </div>
          </form>
        </AirForm>
      </div>
    </div>
  );
}
